import React, { useState, useEffect, useMemo } from 'react';
import { CreditCard, CheckCircle, Clock, AlertCircle, Download, Search, GripVertical, RefreshCw } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api, { authUrl } from '../api';
import { formatEUR } from '../utils/format';
import { MonthContext } from '../context/MonthContext';

const DEFAULT_COLS = [
    { id: 'select', label: '☑', width: '40px' },
    { id: 'employee_name', label: 'Collaborateur' },
    { id: 'date', label: 'Date', type: 'date' },
    { id: 'supplier', label: 'Fournisseur original' },
    { id: 'left_to_pay', label: 'Reste à payer', type: 'number' },
    { id: 'status', label: 'Statut Axonaut' },
    { id: 'comments', label: 'Commentaire' }
];

const SortableHeader = ({ col, sortBy, sortDir, filters, setFilters, handleSort, setPage, allSelected, handleSelectAll, width, onResize }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: col.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        width: width || col.width || '150px',
        minWidth: width || col.width || '150px',
        maxWidth: width || col.width || '150px',
        padding: 0,
        verticalAlign: 'top',
        background: 'var(--surface)',
        borderBottom: '2px solid var(--border)',
        zIndex: transform ? 1 : 0,
        position: 'relative'
    };

    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [col.id]: e.target.value }));
        setPage(1);
    };

    const handleResizeMouseDown = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const startX = e.pageX;
        const startWidth = parseInt(width || col.width || '150', 10);
        
        const handleMouseMove = (moveEvent) => {
            const newWidth = Math.max(50, startWidth + (moveEvent.pageX - startX));
            onResize(`${newWidth}px`);
        };
        
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <th ref={setNodeRef} style={style}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderBottom: '1px solid var(--glass-border)', minHeight: '48px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', flexGrow: 1 }} onClick={() => handleSort(col.id)}>
                    {col.id === 'select' ? (
                        <input 
                            type="checkbox" 
                            checked={allSelected} 
                            onChange={handleSelectAll} 
                            onClick={(e) => e.stopPropagation()} 
                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                        />
                    ) : (
                        <>
                            {col.label} {sortBy === col.id && (sortDir === 'asc' ? '↑' : '↓')}
                        </>
                    )}
                </div>
                {col.id !== 'select' && col.id !== 'actions' && (
                    <div {...attributes} {...listeners} style={{ cursor: 'grab', padding: '2px', opacity: 0.5 }}>
                        <GripVertical size={14} />
                    </div>
                )}
            </div>
            <div style={{ padding: '0.5rem 1rem', minHeight: '52px', display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)' }}>
                {col.id !== 'select' && col.id !== 'actions' && col.type !== 'boolean' && col.type !== 'select' && (
                    <input 
                        type={col.type === 'date' ? 'date' : 'text'} 
                        className="input-field" 
                        style={{ padding: '0.35rem 0.5rem', fontSize: '0.75rem', width: '100%', borderRadius: '6px' }} 
                        placeholder="Filtrer..." 
                        value={filters[col.id] || ''} 
                        onChange={handleFilterChange} 
                    />
                )}
                {col.type === 'select' && (
                    <select className="input-field" style={{ padding: '0.35rem 0.5rem', fontSize: '0.75rem', width: '100%', borderRadius: '6px' }} value={filters[col.id] || ''} onChange={handleFilterChange}>
                        <option value="">Tous</option>
                        {col.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                )}
            </div>
            <div 
                onMouseDown={handleResizeMouseDown}
                style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '6px',
                    cursor: 'col-resize',
                    zIndex: 10,
                    background: 'transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            />
        </th>
    );
};

const ExpenseNotes = () => {
    const { selectedMonth } = React.useContext(MonthContext);
    const [allNotes, setAllNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [processing, setProcessing] = useState(false);

    // Table states
    const [page, setPage] = useState(1);
    const limit = 50;
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [sortDir, setSortDir] = useState('desc');
    const [filters, setFilters] = useState({});
    const [quickFilterEmp, setQuickFilterEmp] = useState('');
    const [hideProcessed, setHideProcessed] = useState(true);

    const [activeCols, setActiveCols] = useState(() => {
        const saved = localStorage.getItem('ndf_cols_v1');
        return saved ? JSON.parse(saved) : DEFAULT_COLS.map(c => c.id);
    });

    const [colWidths, setColWidths] = useState(() => {
        const saved = localStorage.getItem('ndf_col_widths_v1');
        return saved ? JSON.parse(saved) : {};
    });

    const loadNotes = async () => {
        setLoading(true);
        try {
            const url = selectedMonth === 'all' ? '/expense-notes' : `/expense-notes?month_year=${selectedMonth}`;
            const res = await api.get(url);
            setAllNotes(res.data.data || []);
            setSelectedIds([]);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNotes();
    }, [selectedMonth]);

    // Extract unique employees for the quick filter
    const uniqueEmployees = useMemo(() => {
        const names = allNotes.map(n => n.employee_name).filter(Boolean);
        return [...new Set(names)].sort();
    }, [allNotes]);

    // Apply Client-Side Filtering, Searching, and Sorting
    const filteredData = useMemo(() => {
        let result = [...allNotes];

        // 1. Quick filter
        if (quickFilterEmp) {
            result = result.filter(r => r.employee_name === quickFilterEmp);
        }

        // 2. Global search
        if (search) {
            const lowerSearch = search.toLowerCase();
            result = result.filter(r => {
                return (
                    (r.employee_name && r.employee_name.toLowerCase().includes(lowerSearch)) ||
                    (r.supplier && r.supplier.toLowerCase().includes(lowerSearch)) ||
                    (r.comments && r.comments.toLowerCase().includes(lowerSearch)) ||
                    (r.number && r.number.toLowerCase().includes(lowerSearch))
                );
            });
        }

        // 2.5 Hide processed
        if (hideProcessed) {
            result = result.filter(r => !r.is_processed);
        }

        // 3. Column filters
        Object.keys(filters).forEach(key => {
            const val = filters[key]?.toLowerCase();
            if (val) {
                result = result.filter(r => {
                    if (!r[key]) return false;
                    return String(r[key]).toLowerCase().includes(val);
                });
            }
        });

        // 4. Sorting
        result.sort((a, b) => {
            let valA = a[sortBy];
            let valB = b[sortBy];
            if (!valA && valA !== 0) valA = '';
            if (!valB && valB !== 0) valB = '';

            if (sortBy === 'left_to_pay' || sortBy === 'montant_ttc') {
                valA = parseFloat(valA) || 0;
                valB = parseFloat(valB) || 0;
            }

            if (valA < valB) return sortDir === 'asc' ? -1 : 1;
            if (valA > valB) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [allNotes, quickFilterEmp, search, filters, sortBy, sortDir]);

    // Pagination
    const total = filteredData.length;
    const currentData = filteredData.slice((page - 1) * limit, page * limit);

    // Handlers
    const handleSort = (id) => {
        if (id === 'select' || id === 'actions') return;
        if (sortBy === id) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(id);
            setSortDir('asc');
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(currentData.map(d => d.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelect = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(i => i !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const handlePay = async () => {
        setProcessing(true);
        try {
            const expensesToPay = allNotes.filter(n => selectedIds.includes(n.id));
            const res = await api.post('/expense-notes/pay', { expenses: expensesToPay });
            
            if (res.data.pdfUrl) {
                const pdfUrlFull = authUrl(api.defaults.baseURL.replace(/\/api$/, '') + res.data.pdfUrl);
                
                const employeeName = expensesToPay[0]?.employee_name || 'Inconnu';
                const now = new Date();
                const yy = String(now.getFullYear()).slice(-2);
                const mm = String(now.getMonth() + 1).padStart(2, '0');
                const downloadName = `${yy} ${mm} Ndf ${employeeName}.pdf`;
                
                // Tenter d'utiliser la nouvelle API pour forcer la fenêtre "Enregistrer sous"
                if ('showSaveFilePicker' in window) {
                    try {
                        const fileHandle = await window.showSaveFilePicker({
                            suggestedName: downloadName,
                            types: [{
                                description: 'Document PDF',
                                accept: { 'application/pdf': ['.pdf'] }
                            }]
                        });
                        
                        // Télécharger le fichier depuis le serveur
                        const response = await fetch(pdfUrlFull);
                        const blob = await response.blob();
                        
                        // Écrire le fichier à l'emplacement choisi
                        const writable = await fileHandle.createWritable();
                        await writable.write(blob);
                        await writable.close();
                        
                        setSelectedIds([]);
                    } catch (err) {
                        // L'utilisateur a peut-être annulé la boîte de dialogue, on ne fait rien
                        if (err.name !== 'AbortError') {
                            console.error(err);
                        }
                    }
                } else {
                    // Fallback (Safari/Firefox) : téléchargement classique
                    const link = document.createElement('a');
                    link.href = pdfUrlFull;
                    link.setAttribute('download', downloadName);
                    link.setAttribute('target', '_blank');
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode.removeChild(link);
                    
                    setSelectedIds([]);
                }
            } else {
                alert(res.data.message || "Opération terminée.");
            }
            loadNotes();
        } catch (e) {
            const errDetail = e.response?.data?.error || e.message;
            alert("Erreur lors de la génération du document: " + errDetail);
        } finally {
            setProcessing(false);
        }
    };

    const handleQuickToggleProcessed = async (axonautId, isProcessed) => {
        try {
            await api.post('/expense-notes/mark-processed', { ids: [axonautId], is_processed: isProcessed });
            loadNotes();
        } catch (e) {
            console.error("Erreur mise à jour statut", e);
        }
    };

    const handleSelectAllGlobally = () => {
        setSelectedIds(filteredData.map(d => d.id));
    };

    const exportExcel = () => {
        const rows = [
            ['ID', 'Collaborateur', 'Numéro', 'Date', 'Fournisseur', 'Reste à payer', 'Statut', 'Commentaire'],
            ...filteredData.map(r => [
                r.id,
                r.employee_name,
                r.number,
                r.date,
                r.supplier,
                r.left_to_pay,
                'A payer',
                r.comments
            ])
        ];
        
        let csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
            + rows.map(e => e.join(";")).join("\n");
            
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "export_notes_de_frais.csv");
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    };

    // Columns Management
    const columns = activeCols.map(id => DEFAULT_COLS.find(c => c.id === id)).filter(Boolean);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleResizeColumn = (colId, newWidth) => {
        setColWidths(prev => {
            const next = { ...prev, [colId]: newWidth };
            localStorage.setItem('ndf_col_widths_v1', JSON.stringify(next));
            return next;
        });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setActiveCols((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                const newArray = arrayMove(items, oldIndex, newIndex);
                localStorage.setItem('ndf_cols_v1', JSON.stringify(newArray));
                return newArray;
            });
        }
    };

    // Cell Rendering
    const renderCell = (col, row) => {
        if (col.id === 'select') {
            return <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => handleSelect(row.id)} style={{ cursor: 'pointer', width: '18px', height: '18px' }} />;
        }
        if (col.id === 'employee_name') {
            return (
                <div>
                    <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                        {row.employee_name || <span style={{ color: 'var(--error)' }}><AlertCircle size={14} style={{display:'inline', verticalAlign:'middle', marginRight:'4px'}}/> Manquant</span>}
                    </span>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>{row.number}</div>
                </div>
            );
        }
        if (col.id === 'date') {
            return <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{row.date ? new Date(row.date).toLocaleDateString('fr-FR') : '-'}</span>;
        }
        if (col.id === 'supplier') {
            return <span style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>{row.supplier || row.title || '-'}</span>;
        }
        if (col.id === 'left_to_pay') {
            return <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{formatEUR(row.left_to_pay) || '0,00 €'}</span>;
        }
        if (col.id === 'status') {
            if (row.is_processed) {
                return (
                    <span className="badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#059669', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleQuickToggleProcessed(row.axonaut_expense_id, 0); }} title="Marquer comme À Payer">
                        <CheckCircle size={12}/> Traitée
                    </span>
                );
            }
            return (
                <span className="badge" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#d97706', display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handleQuickToggleProcessed(row.axonaut_expense_id, 1); }} title="Marquer comme Traitée">
                    <Clock size={12}/> À payer
                </span>
            );
        }
        if (col.id === 'comments') {
            return <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{row.comments || '-'}</span>;
        }
        return row[col.id] || '-';
    };

    const renderPagination = () => (
        <div className="pagination">
            <div>
                Affichage de {(page - 1) * limit + (total > 0 ? 1 : 0)} à {Math.min(page * limit, total)} sur {total}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn-icon" disabled={page === 1} onClick={() => setPage(page - 1)}>&lt;</button>
                <button className="btn-icon" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>&gt;</button>
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%' }}>
            <div className="header" style={{ marginBottom: '1rem' }}>
                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 0.5rem 0' }}>Notes de Frais</h1>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>Validez et payez les notes de frais de vos collaborateurs via Qonto.</p>
            </div>

            <div className="table-toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', margin: 0, marginBottom: '0.5rem', background: 'var(--surface)', padding: '1rem', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
                {/* Recherche globale */}
                <div style={{ position: 'relative', width: '250px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                    <input 
                        type="text" placeholder="Recherche globale..." className="input-field" 
                        style={{ paddingLeft: '2.2rem', width: '100%', height: '36px', borderRadius: '8px' }}
                        value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                    />
                </div>

                {/* Filtres Rapides */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
                        <button 
                            onClick={() => { setHideProcessed(!hideProcessed); setPage(1); }}
                            style={{ 
                                padding: '4px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer', border: 'none', transition: 'all 0.2s ease',
                                background: hideProcessed ? '#f59e0b' : 'transparent',
                                color: hideProcessed ? 'white' : 'var(--text-primary)',
                            }}
                        >
                            N'afficher que les "À payer"
                        </button>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
                        <button 
                            onClick={() => { setQuickFilterEmp(''); setPage(1); }}
                            style={{ 
                                padding: '4px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer', border: 'none', transition: 'all 0.2s ease',
                                background: quickFilterEmp === '' ? 'var(--accent)' : 'transparent',
                                color: quickFilterEmp === '' ? 'white' : 'var(--text-primary)',
                            }}
                        >
                            Tous
                        </button>
                        {uniqueEmployees.map(emp => (
                            <button 
                                key={emp}
                                onClick={() => { setQuickFilterEmp(emp); setPage(1); }}
                                style={{ 
                                    padding: '4px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer', border: 'none', transition: 'all 0.2s ease',
                                    background: quickFilterEmp === emp ? 'var(--accent)' : 'transparent',
                                    color: quickFilterEmp === emp ? 'white' : 'var(--text-primary)',
                                }}
                            >
                                {emp}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Boutons d'action */}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ background: 'var(--bg-secondary)', padding: '0 10px', height: '36px', borderRadius: '8px', border: '1px solid var(--glass-border)', fontSize: '0.85rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center' }}>
                        <strong>{total}</strong> &nbsp;note(s)
                    </div>
                    <button 
                        className="btn" 
                        style={{ background: '#3b82f6', color: '#ffffff', border: 'none', display: 'flex', gap: '6px', alignItems: 'center', opacity: selectedIds.length === 0 || processing ? 0.4 : 1, width: 'auto', fontWeight: 600, padding: '0 12px', height: '36px', fontSize: '13px', borderRadius: '8px', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease' }}
                        disabled={selectedIds.length === 0 || processing}
                        onClick={handlePay}
                    >
                        <CreditCard size={16} /> {processing ? 'Création...' : `Générer PDF (${selectedIds.length})`}
                    </button>
                    <button 
                        className="btn" 
                        style={{ background: 'var(--success)', color: 'white', border: 'none', height: '36px', padding: '0 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '8px', opacity: selectedIds.length === 0 || processing ? 0.4 : 1, cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease' }}
                        disabled={selectedIds.length === 0 || processing}
                        onClick={async () => {
                            try {
                                await api.post('/expense-notes/mark-processed', { ids: selectedIds, is_processed: true });
                                setSelectedIds([]);
                                loadNotes();
                            } catch (e) { alert("Erreur."); }
                        }}
                        title="Marquer ces dépenses comme Traitées"
                    >
                        <CheckCircle size={16} /> Marquer traitée
                    </button>
                    <button 
                        className="btn" 
                        style={{ background: '#f59e0b', color: 'white', border: 'none', height: '36px', padding: '0 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '8px', opacity: selectedIds.length === 0 || processing ? 0.4 : 1, cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease' }}
                        disabled={selectedIds.length === 0 || processing}
                        onClick={async () => {
                            try {
                                await api.post('/expense-notes/mark-processed', { ids: selectedIds, is_processed: false });
                                setSelectedIds([]);
                                loadNotes();
                            } catch (e) { alert("Erreur."); }
                        }}
                        title="Marquer ces dépenses comme Non Traitées"
                    >
                        <Clock size={16} /> Marquer non traitée
                    </button>
                    <button className="btn" style={{ background: '#8b5cf6', color: 'white', border: 'none', height: '36px', padding: '0 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '8px', transition: 'all 0.2s ease' }} onClick={loadNotes} title="Rafraîchir les données">
                        <RefreshCw size={16} /> Actualiser
                    </button>
                    <button className="btn" style={{ background: 'var(--success)', color: 'white', border: 'none', height: '36px', padding: '0 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '8px', transition: 'all 0.2s ease' }} onClick={exportExcel}>
                        <Download size={16} /> Excel
                    </button>
                </div>
            </div>

            {selectedIds.length > 0 && selectedIds.length === currentData.length && total > currentData.length && selectedIds.length < total && (
                <div style={{ background: '#e0f2fe', color: '#0369a1', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0', border: '1px solid #7dd3fc', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                    <span>Toutes les <strong>{selectedIds.length}</strong> notes de cette page sont sélectionnées.</span>
                    <button className="btn" onClick={handleSelectAllGlobally} style={{ background: '#ffffff', color: '#0369a1', border: '1px solid #bae6fd', fontWeight: '500' }}>
                        Sélectionner les {total} notes de cette recherche ?
                    </button>
                </div>
            )}

            {renderPagination()}

            <div className="glass-panel data-table-wrapper" style={{ overflowX: 'auto', display: 'flex', flexDirection: 'column', flex: 1, padding: 0 }}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <table style={{ minWidth: 'max-content', width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <SortableContext items={activeCols} strategy={horizontalListSortingStrategy}>
                                <tr>
                                    {columns.map(col => (
                                        <SortableHeader 
                                            key={col.id} 
                                            col={col} 
                                            width={colWidths[col.id] || col.width || '150px'}
                                            onResize={(newWidth) => handleResizeColumn(col.id, newWidth)}
                                            sortBy={sortBy} 
                                            sortDir={sortDir} 
                                            filters={filters} 
                                            setFilters={setFilters} 
                                            handleSort={handleSort}
                                            setPage={setPage}
                                            allSelected={currentData.length > 0 && selectedIds.length === currentData.length}
                                            handleSelectAll={handleSelectAll}
                                        />
                                    ))}
                                </tr>
                            </SortableContext>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={columns.length} style={{ padding: '2rem', textAlign: 'center' }}>Chargement...</td></tr>
                            ) : currentData.length === 0 ? (
                                <tr><td colSpan={columns.length} style={{ padding: '2rem', textAlign: 'center' }}>Aucune donnée correspondante.</td></tr>
                            ) : currentData.map(row => (
                                <tr key={row.id} style={{ borderBottom: '1px solid var(--glass-border)', background: row.is_processed ? 'rgba(16, 185, 129, 0.03)' : 'transparent' }}>
                                    {columns.map(col => (
                                        <td key={col.id} title={row[col.id] ? String(row[col.id]) : ''} style={{ padding: '1rem', width: colWidths[col.id] || col.width || '150px', minWidth: colWidths[col.id] || col.width || '150px', maxWidth: colWidths[col.id] || col.width || '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {renderCell(col, row)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </DndContext>
            </div>
            
            <div style={{ marginTop: '-1rem' }}>
                {renderPagination()}
            </div>
        </div>
    );
};

export default ExpenseNotes;
